import {
    type ComponentProps,
    type DetailedHTMLProps,
    forwardRef,
    type HTMLAttributes,
    type JSXElementConstructor,
    type ReactNode,
} from "react";

import { cn } from "@btnet/utilities-core";

import { type IEditor } from "commonComponents/btWrappers/editor/core/useBTEditor.types";

import "./InputRecaptureHack.less";

type NonConflictingDivProps = Omit<
    DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
    "contentEditable" | "onFocus" | "onBeforeInput"
>;

interface IInputRecaptureHackProps extends NonConflictingDivProps {
    editor: IEditor;
    children: ReactNode;
}

/**
 * Whoever comes across this, I'm sorry (◡︵◡)
 *
 * This is a hack to fix the issue where the editor loses focus when you click on the control bar.
 * We need this to support mobile devices, because loosing focus temporarally hides the keyboard,
 * causing layout shifts.
 */
export const InputRecaptureHack = forwardRef<HTMLDivElement, IInputRecaptureHackProps>(
    ({ children, editor, className, ...otherProps }, ref) => {
        // Minimize side effects by only doing this on touch devices. For example, certain browser extensions
        // might do some weird things because we're prettending like this is an input element. This still
        // might activate for devices that have a physical keyboard, but it's better than nothing.
        const isTouchEnabled = "ontouchstart" in window || navigator.maxTouchPoints > 0;
        return (
            <div
                {...otherProps}
                ref={ref}
                contentEditable={isTouchEnabled}
                suppressContentEditableWarning={true}
                className={cn("InputRecaptureHack", className)}
                onBeforeInput={(e) => {
                    // Shouldn't need this because we prevent default on focus, but just incase let's make sure
                    // users can't ACTUALLY edit elements they aren't really supposed to...
                    isTouchEnabled && e.preventDefault();
                }}
                onFocus={
                    isTouchEnabled
                        ? (e) => {
                              editor.tiptapEditor.commands.focus();
                              e.preventDefault();
                          }
                        : undefined
                }
            >
                {children}
            </div>
        );
    },
);

interface IInputRecaptureHackMenuItemProps<
    C extends keyof JSX.IntrinsicElements | JSXElementConstructor<ComponentProps<C>>,
> {
    editor: IEditor;
    menuComponent: C;
    menuProps: ComponentProps<C>;
    children?: ReactNode;
}

export function InputRecaptureHackMenuItem<
    C extends keyof JSX.IntrinsicElements | JSXElementConstructor<ComponentProps<C>>,
>({
    menuComponent: MenuComponent,
    menuProps,
    editor,
    children,
    ...menuGhostProps
}: IInputRecaptureHackMenuItemProps<C>) {
    return (
        <InputRecaptureHack editor={editor}>
            <MenuComponent {...menuProps} {...(menuGhostProps as any)}>
                {children}
            </MenuComponent>
        </InputRecaptureHack>
    );
}
